import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const fetchFacebookPages = createAsyncThunk(
  'facebook/fetchPages',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/facebook/pages');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch pages');
    }
  }
);

export const fetchFacebookForms = createAsyncThunk(
  'facebook/fetchForms',
  async (pageId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/facebook/forms/${pageId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch lead forms');
    }
  }
);

export const fetchFormFields = createAsyncThunk(
  'facebook/fetchFormFields',
  async ({ formId, pageId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/facebook/form-fields/${formId}`, { params: { pageId } });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to fetch form fields');
    }
  }
);

export const subscribeFacebookPage = createAsyncThunk(
  'facebook/subscribePage',
  async (pageId, { rejectWithValue }) => {
    try {
      const res = await api.post(`/facebook/subscribe/${pageId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to subscribe page');
    }
  }
);

const facebookSlice = createSlice({
  name: 'facebook',
  initialState: {
    pages: [],
    forms: [],
    formFields: [],
    pagesLoading: false,
    formsLoading: false,
    fieldsLoading: false,
    subscribing: false,
    subscribeSuccess: false,
    error: null,
  },
  reducers: {
    clearForms(state) {
      state.forms = [];
      state.formFields = [];
    },
    clearFormFields(state) {
      state.formFields = [];
    },
    clearSubscribeSuccess(state) {
      state.subscribeSuccess = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFacebookPages.pending, (state) => {
        state.pagesLoading = true;
        state.error = null;
      })
      .addCase(fetchFacebookPages.fulfilled, (state, action) => {
        state.pagesLoading = false;
        state.pages = action.payload;
      })
      .addCase(fetchFacebookPages.rejected, (state, action) => {
        state.pagesLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFacebookForms.pending, (state) => {
        state.formsLoading = true;
        state.error = null;
      })
      .addCase(fetchFacebookForms.fulfilled, (state, action) => {
        state.formsLoading = false;
        state.forms = action.payload;
      })
      .addCase(fetchFacebookForms.rejected, (state, action) => {
        state.formsLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFormFields.pending, (state) => {
        state.fieldsLoading = true;
        state.error = null;
      })
      .addCase(fetchFormFields.fulfilled, (state, action) => {
        state.fieldsLoading = false;
        state.formFields = action.payload;
      })
      .addCase(fetchFormFields.rejected, (state, action) => {
        state.fieldsLoading = false;
        state.error = action.payload;
      })
      .addCase(subscribeFacebookPage.pending, (state) => {
        state.subscribing = true;
        state.subscribeSuccess = false;
        state.error = null;
      })
      .addCase(subscribeFacebookPage.fulfilled, (state) => {
        state.subscribing = false;
        state.subscribeSuccess = true;
      })
      .addCase(subscribeFacebookPage.rejected, (state, action) => {
        state.subscribing = false;
        state.error = action.payload;
      });
  },
});

export const { clearForms, clearFormFields, clearSubscribeSuccess, clearError } = facebookSlice.actions;
export default facebookSlice.reducer;
